import { $dfsIterator, $findMatchingParent } from '@lexical/utils';
import { $getRoot, $getSelection, $isElementNode, $isRangeSelection, LexicalNode } from 'lexical';
import { $isCustomHeadingNode, CustomHeadingNode } from 'modules/Lexical/nodes/CustomHeadingNode';
import { $isCustomListItemNode, CustomListItemNode } from 'modules/Lexical/nodes/CustomListItemNode';
import { $isCustomParagraphNode, CustomParagraphNode } from 'modules/Lexical/nodes/CustomParagraphNode';
import * as Constants from 'const';
import { toPx } from 'utils/toPx';
import { TextBlockProps } from '../types';

export type NodeWithLineHeight = CustomParagraphNode | CustomListItemNode | CustomHeadingNode;

export function $hasLineHeight(node: LexicalNode | null): node is NodeWithLineHeight {
  if (!node) {
    return false;
  }
  const isParagraph = $isCustomParagraphNode(node);
  const isList = $isCustomListItemNode(node);
  const isHeadingNode = $isCustomHeadingNode(node);

  return isParagraph || isList || isHeadingNode;
}

export function $getNearestBlockElementAncestor(startNode: LexicalNode): LexicalNode | null {
  return $findMatchingParent(startNode, node => $isElementNode(node) && !node.isInline());
}

export function $getBlockLineHeightFromSelection(): TextBlockProps['blockLineHeight'] {
  const selection = $getSelection();

  if ($isRangeSelection(selection)) {
    const heights = selection.getNodes()
      .map($getNearestBlockElementAncestor)
      .filter($hasLineHeight)
      .map((node: NodeWithLineHeight) => node.getLineHeight() ?? Constants.DefaultLineHeight);

    if (heights.length) {
      const [firstHeight] = heights;
      const uniqueHeights = new Set(heights);

      return uniqueHeights.size === 1 ? firstHeight : undefined;
    }
  }

  return Constants.DefaultLineHeight;
}

export function $applyLineHeight(
  node: LexicalNode | null,
  value: Constants.TextLineHeightValue | undefined,
): void {
  if (!value || !$hasLineHeight(node)) {
    return;
  }
  node.setLineHeight(value);
}

const nodeHasCorrectOrMissingLineHeight = (
  node: LexicalNode,
  lineHeight: number,
): boolean => {
  const value = $hasLineHeight(node) && node.getLineHeight();

  return !value || value === lineHeight;
};

const nodeHasCorrectLineHeight = (
  node: NodeWithLineHeight,
  lineHeight: number,
  skipEmpty: boolean,
): boolean => {
  const value = node.getLineHeight();

  return value === undefined ? skipEmpty : value === lineHeight;
};

export function $doAllNodesHaveLineHeight(
  lineHeight: number,
  skipEmpty: boolean,
): boolean {
  let count = 0;
  let checkedDepth = Infinity;

  for (const { depth, node } of $dfsIterator($getRoot())) {
    if (depth > checkedDepth) {
      if (!nodeHasCorrectOrMissingLineHeight(node, lineHeight)) {
        return false;
      }
      continue;
    }

    if (depth <= checkedDepth) {
      checkedDepth = Infinity;
    }

    if ($hasLineHeight(node)) {
      if (!nodeHasCorrectLineHeight(node, lineHeight, skipEmpty)) {
        return false;
      }
      count += 1;
      checkedDepth = depth;
    }
  }

  return count > 0;
}

export function $applyLineHeightToAllNodes(lineHeight: number): void {
  for (const { node } of $dfsIterator($getRoot())) {
    if ($hasLineHeight(node)) {
      node.setLineHeight(lineHeight);
    }
  }
}

const LINE_HEIGHT_CSS_VARIABLE = '--custom-lineheight';
const LINE_HEIGHT_DEFAULT_VALUE = Constants.DefaultLineHeight;
const DEFAULT_FONT_SIZE = Constants.DefaultCustomStyle.FONT_SIZE as number;

export function setNodeLineHeightToDOM(
  element: HTMLElement,
  lineHeight: number | undefined,
  fontSize = DEFAULT_FONT_SIZE,
): void {
  element.style.lineHeight = toPx((lineHeight ?? LINE_HEIGHT_DEFAULT_VALUE) * fontSize);
  element.style.setProperty(LINE_HEIGHT_CSS_VARIABLE, lineHeight?.toString() ?? null);
}
